import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

export function SectionHeader({ title, subtitle, to, action = "View all" }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-5 sm:mb-6">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-display">{title}</h2>
        {subtitle && <p className="text-sm sm:text-base text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {to && (
        <Link to={to} className="shrink-0 text-sm font-semibold text-amber-600 hover:text-amber-700 whitespace-nowrap">
          {action} →
        </Link>
      )}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, text, children }) {
  return (
    <div className="text-center py-16 px-4" data-testid="empty-state">
      {Icon && <div className="mx-auto h-14 w-14 rounded-2xl bg-slate-100 grid place-items-center mb-4"><Icon className="h-7 w-7 text-slate-400" /></div>}
      <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
      {text && <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">{text}</p>}
      {children && <div className="mt-5">{children}</div>}
    </div>
  );
}

/**
 * Shown when a request genuinely failed (network/CORS/5xx) so that a failure is
 * never rendered as an empty catalogue or as fake placeholder content.
 */
export function ErrorNotice({ title = "We couldn't load this content", text, onRetry, retryLabel = "Try again" }) {
  return (
    <div className="text-center py-16 px-4" role="alert" data-testid="api-error-notice">
      <div className="mx-auto h-14 w-14 rounded-2xl bg-amber-50 grid place-items-center mb-4">
        <AlertTriangle className="h-7 w-7 text-amber-500" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
      {text && <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">{text}</p>}
      {onRetry && (
        <button type="button" onClick={onRetry} data-testid="api-error-retry"
          className="mt-5 bg-slate-900 text-white font-semibold px-5 py-2.5 rounded-lg">
          {retryLabel}
        </button>
      )}
    </div>
  );
}

/**
 * The business name comes only from the backend settings. Until it arrives we show
 * a neutral skeleton; if it never arrives we show nothing rather than inventing a
 * brand name (no "STORE NAME" or hardcoded fallback branding).
 */
export function BusinessName({ name, loading }) {
  if (name) return name;
  if (loading) {
    return <span aria-hidden="true" className="inline-block h-4 w-24 rounded bg-slate-200 animate-pulse align-middle" />;
  }
  return <span className="sr-only">Store name unavailable</span>;
}

export function PageHead({ title, crumbs }) {
  return (
    <div className="mb-6">
      {crumbs && (
        <nav className="text-xs text-slate-500 flex items-center gap-1.5 mb-2 flex-wrap">
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {c.to ? <Link to={c.to} className="hover:text-amber-600">{c.label}</Link> : <span className="text-slate-700">{c.label}</span>}
              {i < crumbs.length - 1 && <span>/</span>}
            </span>
          ))}
        </nav>
      )}
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-display">{title}</h1>
    </div>
  );
}
